import { useRef, useState } from 'react';

const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
const money = cents => `₹${((cents || 0) / 100).toFixed(2)}`;

export default function FinanceCloseDialog({ account, onClose, onSubmit }) {
  const [date, setDate] = useState(today);
  const [method, setMethod] = useState('Cash');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(null);
  const lock = useRef(false);
  const earliest = (account.receipts || []).reduce((latest, row) => row.date > latest ? row.date : latest, account.disbursedDate);
  const remaining = Math.max(0, account.outstandingCents || 0);
  const submit = async event => {
    event.preventDefault();
    if (lock.current) return;
    if (!pending.current) pending.current = { requestId: crypto.randomUUID(), amount: (remaining / 100).toFixed(2), date, method, notes, updatedAt: account.updatedAt };
    lock.current = true; setBusy(true); setError('');
    try { await onSubmit(pending.current); onClose(); }
    catch (err) {
      if (err.status >= 400 && err.status < 500 && err.status !== 408) pending.current = null;
      setError(`${err.message}${pending.current ? ' Retry will use the same payment to avoid duplicates.' : ''}`);
    } finally { lock.current = false; setBusy(false); }
  };
  return <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/50 p-4">
    <form role="dialog" aria-modal="true" aria-label="Close finance account" onSubmit={submit} className="max-h-[90vh] w-full max-w-lg space-y-4 overflow-auto rounded-xl bg-white p-5">
      <h3 className="text-xl font-bold">Close finance account</h3>
      <p>{account.name} · {account.reference}</p>
      <dl className="space-y-1 rounded border bg-slate-50 p-3">
        <div>Total repayment: <strong>{money(account.repaymentCents)}</strong></div>
        <div>Already collected: <strong>{money(account.receivedCents)}</strong></div>
        <div>Final payment to collect: <strong>{money(remaining)}</strong></div>
      </dl>
      <p className="text-sm">This applies existing credit and the final payment to all remaining installments, including future dates, and closes the account. Payment history is kept.</p>
      {remaining === 0 && <p className="text-sm text-emerald-800">Fully paid. Closing will not record another payment.</p>}
      {(account.poolCents || 0) > (account.schedule || []).reduce((sum, row) => sum + row.pendingCents, 0) && <p className="text-sm">Any credit beyond the total repayment will remain recorded in the overpayment pool.</p>}
      {error && <p role="alert" className="text-red-700">{error}</p>}
      <fieldset disabled={busy || Boolean(pending.current)} className="space-y-3">
        <label className="block">Closure date<input required type="date" min={earliest} max={today()} value={date} onChange={event => setDate(event.target.value)} className="mt-1 block w-full rounded border p-2" /></label>
        <label className="block">Final payment method<select value={method} onChange={event => setMethod(event.target.value)} className="mt-1 block w-full rounded border p-2">{['Cash', 'UPI', 'Bank Transfer', 'Card'].map(value => <option key={value}>{value}</option>)}</select></label>
        <label className="block">Closure notes<textarea maxLength={500} value={notes} onChange={event => setNotes(event.target.value)} className="mt-1 block w-full rounded border p-2" /></label>
      </fieldset>
      <div className="flex justify-end gap-2">
        <button type="button" disabled={busy} onClick={onClose} className="rounded border px-3 py-2">Cancel</button>
        <button type="submit" disabled={busy} className="rounded bg-emerald-700 px-3 py-2 font-semibold text-white disabled:opacity-50">{busy ? 'Closing…' : pending.current ? 'Retry closure' : remaining ? `Collect ${money(remaining)} & close` : 'Close fully paid account'}</button>
      </div>
    </form>
  </div>;
}
